import { Spacer, VDBText } from "@components/atoms";
import React, { useState } from "react";
import { Pressable } from "react-native";
import styled from "styled-components/native";
import { Image } from "expo-image";
import { SpaceType } from "@components/atoms/Spacer/types";
import VDBIcon from "@components/icons/VDBIcon";
import { IconsVariant } from "@components/icons/VDBIcon";
import { DefaultTheme, useTheme } from "styled-components/native";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { isPair } from "@utils/product/productValidators";
import { VDBIcons } from "@utils/VDBIcons";
import { useTranslation } from "react-i18next";
import {
  getImageSource,
  getIOSImageProps,
  ImageSizePreset,
} from "@utils/image/imageOptimizer";
import { FALLBACK_IMAGE_ASSET } from "@utils/product/searchResultConstants";
import { Diamond } from "@src/types/DiamondDetail";
import EmptyImagePreview from "@components/molecules/GalleryView/utils/EmptyImage/EmptyImagePreview";
import { getShapeValue } from "@components/molecules/GalleryView/utils/EmptyImage/utils";
import {
  getOptimizedListImageUrl,
  isImageUrlEmptyOrUndefined,
} from "@components/molecules/Utils";

type ImageComponentJewelryProps = {
  mItem: Diamond;
  addNewConfig: () => void;
};

const ImageComponentJewelry = ({
  mItem,
  addNewConfig,
}: ImageComponentJewelryProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const typography = useTypographyStore();
  const [imageUri, setImageUri] = useState(
    !isImageUrlEmptyOrUndefined(mItem)
      ? getOptimizedListImageUrl(mItem)
      : FALLBACK_IMAGE_ASSET
  );
  const [showImageContainer, setShowImageContainer] = useState(
    !isImageUrlEmptyOrUndefined(mItem)
  );
  const [showErrorImage, setShowErrorImage] = useState(
    isImageUrlEmptyOrUndefined(mItem)
  );

  return (
    <ImageAndButtonContainer>
      <ImageAndContentRow>
        <ImageResourceContainer>
          {showErrorImage ? (
            <FallbackImageContainer theme={theme}>
              <EmptyImagePreview
                shape={getShapeValue(mItem)}
                size={30}
                isPair={isPair(mItem) ?? false}
              />
            </FallbackImageContainer>
          ) : showImageContainer ? (
            <Image
              style={{
                height: 48,
                width: 48,
                borderRadius: 8,
                borderColor: theme["core-03"],
                borderWidth: 1,
              }}
              source={getImageSource(
                imageUri || FALLBACK_IMAGE_ASSET,
                ImageSizePreset.LIST_THUMB
              )}
              contentFit="cover"
              {...getIOSImageProps()}
              onError={() => {
                if (mItem?.video_url) {
                  setShowImageContainer(false);
                } else {
                  setShowErrorImage(true);
                  setShowImageContainer(false);
                  setImageUri(FALLBACK_IMAGE_ASSET);
                }
              }}
            />
          ) : (
            <VideoBackgroundContainer theme={theme}>
              <VideoPlayIconContainer theme={theme}>
                <VDBIcon
                  color={theme["core-06"]}
                  size={20}
                  variant={IconsVariant.VDBCustom}
                  name={VDBIcons.PLAY}
                />
              </VideoPlayIconContainer>
            </VideoBackgroundContainer>
          )}
        </ImageResourceContainer>
        <FirstContentContainer theme={theme}>
          {mItem?.short_title && (
            <>
              <VDBText
                type={typography["Minor-M"]}
                color={theme["core-05"]}
                numberOfLines={2}
                ellipsizeMode="tail"
              >
                {mItem?.short_title}
              </VDBText>
              <Spacer size={SpaceType.ExtraSmall} />
            </>
          )}
          {mItem?.stock_num && (
            <TitleContainer>
              <Spacer size={SpaceType.ExtraSmall} />
              <TextContainer>
                <VDBText
                  type={{ ...typography["Minor-R"], flexShrink: 0 }}
                  color={theme["core-06"]}
                  numberOfLines={1}
                  ellipsizeMode="tail"
                >
                  {t("searchResult.listView.style.text", "Style #")}:
                </VDBText>
                <Spacer size={SpaceType.ExtraSmall} />
                <VDBText
                  type={{ ...typography["Minor-M"], flex: 1, minWidth: 0 }}
                  color={theme["core-05"]}
                >
                  {mItem?.stock_num}
                </VDBText>
              </TextContainer>
            </TitleContainer>
          )}
        </FirstContentContainer>
        <AddConfigurationButton theme={theme} onPress={() => addNewConfig()}>
          <VDBText type={typography["Body-M"]} color={theme["jewelry-06"]}>
            {theme.isMobile
              ? t("searchResult.jewelry.add.text", "Add")
              : t(
                  "searchResult.jewelry.addConfiguration.text",
                  "Add Configuration"
                )}
          </VDBText>
          <VDBIcon
            name={VDBIcons.ADD_CIRCLE}
            variant={IconsVariant.VDBCustom}
            size={18}
            color={theme["jewelry-06"]}
          />
        </AddConfigurationButton>
      </ImageAndContentRow>
    </ImageAndButtonContainer>
  );
};

const FirstContentContainer = styled.View<any>`
  flex: 1;
  min-width: 0;
  margin-start: 8px;
  justify-content: center;
  align-items: flex-start;
`;

const ImageAndButtonContainer = styled.View`
  height: auto;
  padding: 8px;
  width: 100%;
`;

const ImageAndContentRow = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const ImageResourceContainer = styled.View`
  padding-left: 2px;
  justify-content: center;
`;

const TitleContainer = styled.View<any>`
  width: 100%;
`;

const VideoPlayIconContainer = styled.View<any>`
  height: 28px;
  width: 28px;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: ${({ theme }) => theme["over-02"]};
  border-radius: 14px;
`;

const VideoBackgroundContainer = styled.View<any>`
  height: 48px;
  width: 48px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme["core-01"]};
`;

const FallbackImageContainer = styled.View<any>`
  width: 48px;
  height: 48px;
  background-color: ${({ theme }) => theme["core-01"]};
  border-radius: 8px;
  justify-content: center;
  align-items: center;
  border-color: ${({ theme }) => theme["core-03"]};
  border-width: 1px;
`;

const TextContainer = styled.View`
  flex-direction: row;
  align-items: flex-start;
  width: 100%;
  min-width: 0;
`;

const AddConfigurationButton = styled(Pressable)<{ theme: DefaultTheme }>`
  width: ${({ theme }) => (theme.isMobile ? `94px` : `180px`)};
  height: 32px;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-horizontal: 16px;
  border-radius: 8px;
  background-color: "transparent";
  border-color: ${({ theme }) => theme["jewelry-06"]};
  border-width: 2px;
`;

export default React.memo(ImageComponentJewelry);
